import type {
  ColDef,
  EditableCallbackParams,
  ICellEditorParams,
} from 'ag-grid-community';
import { taskValueGetter } from 'components/organisms/task-grid/plugins/with-task-column/task-value-getter';
import { getEditor } from 'components/organisms/tasks/editors';
import { getRenderer } from 'components/organisms/tasks/renderers';
import type { StatusField } from 'stores/fields';
import type { FilterableField } from '../fields';
import { suppressEnterKey } from '../utils';
import { getIsEditable } from './column';

export const taskColumn = (
  fields: FilterableField[],
  editable = true,
  initialWidth = 400,
): ColDef => {
  const statusField = fields.find(
    (field) => field.fieldName === 'status',
  ) as StatusField | null;

  const isEditableFn =
    statusField == null
      ? editable
      : (params: EditableCallbackParams) => {
          if (params.node.id === 'provisional') {
            return true;
          }

          return getIsEditable(statusField, editable, () => true)(params);
        };

  return {
    field: 'status',
    headerName: 'Task',
    cellRenderer: 'agGroupCellRenderer',
    editable: isEditableFn,
    cellRendererParams: {
      suppressCount: true,
      suppressDoubleClickExpand: true,
      fields,
      innerRenderer: statusField == null ? undefined : getRenderer(statusField),
    },
    cellEditor: statusField == null ? undefined : getEditor(statusField),
    cellEditorPopupPosition: 'under',
    cellEditorSelector: ({ node }: ICellEditorParams) => {
      if (node.id === 'provisional') {
        return {
          component: statusField == null ? undefined : getEditor(statusField),
          popup: false,
        };
      }

      return undefined;
    },
    valueGetter: taskValueGetter,
    initialWidth,
    comparator: (a, b, nodeA, nodeB) => {
      const a_status = (a?.status as number) ?? -1;
      const b_status = (b?.status as number) ?? -1;
      const a_name = (nodeA?.data?.taskName as string) ?? '';
      const b_name = (nodeB?.data?.taskName as string) ?? '';

      return a_status === b_status
        ? a_name === b_name
          ? 0
          : a_name < b_name
          ? -1
          : 1
        : a_status < b_status
        ? -1
        : 1;
    },
    lockPinned: true,
    lockPosition: true,
    pinned: 'left',
    showRowGroup: true,
    cellStyle: {
      borderRight: '1px solid var(--table-border-color)',
      cursor: 'pointer',
    },
    suppressKeyboardEvent: suppressEnterKey,
  };
};
